// The 911 page (DESIGN.md "911 page and 911 contacts", mockup l; spec sections 4.1 and 4.3,
// story TC15): offices with their confirmed networks and Kite's proposals to confirm or dismiss,
// the 911 contacts (every Admin by default, plus outside emails), and where Kite places each
// person right now. Admins only: the routes answer 403 to anyone else (TC12, TC14).

import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Avatar,
  IconButton,
  InfoButton,
  Menu,
  PeoplePicker,
  roleNames,
  Surface,
  Table,
  TBody,
  Td,
  THead,
  Th,
  ToggleChip,
  Tooltip,
  Tr,
} from "@vd/ui";
import { Building2, Mail, Plus, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { usersQuery } from "../../../api/admin";
import { errorMessage } from "../../../api/client";
import {
  type EmergencyContacts,
  type EmergencyLocationsPage,
  e911,
  e911ContactsQuery,
  e911Keys,
  e911PageQuery,
  type Office,
  type OfficeNetwork,
  type PersonPlace,
} from "../../../api/e911";
import { purchaseSettingsQuery } from "../../../api/numbers";
import { requireAdmin } from "../../../auth/useMe";
import { AddressDialog } from "../../../components/numbers/AddressDialog";
import { toast } from "../../../components/toast";
import { Badge, Button, ErrorState, Input, Loading, PageHeader } from "../../../components/ui";

export const Route = createFileRoute("/admin/phone-system/911")({
  staticData: { crumb: { label: "911", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(e911PageQuery),
      context.queryClient.prefetchQuery(e911ContactsQuery),
    ]),
  component: E911Page,
});

function E911Page() {
  const q = useQuery(e911PageQuery);
  return (
    <>
      <PageHeader title="911" />
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,410px)]">
        <OfficesCard q={q} />
        <ContactsCard />
        <div className="lg:col-span-2">
          <PeopleCard q={q} />
        </div>
      </div>
    </>
  );
}

type PageQuery = UseQueryResult<EmergencyLocationsPage>;

/** A card whose (?) sits beside the heading, outside it, so the heading reads as its title. */
function Panel({
  title,
  help,
  actions,
  children,
}: {
  title: string;
  help: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Surface asChild className="flex min-w-0 flex-col">
      <section aria-label={title}>
        <header className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 pt-2">
          <div className="flex items-center gap-1.5">
            <h2 className="font-medium text-title">{title}</h2>
            <InfoButton kind="help" label={`About ${title}`}>
              {help}
            </InfoButton>
          </div>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </header>
        {children}
      </section>
    </Surface>
  );
}

function PageState({ q }: { q: PageQuery }) {
  return q.isPending ? <Loading /> : <ErrorState error={q.error} onRetry={() => q.refetch()} />;
}

/** `198.51.100.14/32` reads as the address alone; a range keeps its prefix. */
function networkText(cidr: string): string {
  return cidr.replace(/\/(32|128)$/, "");
}

// Offices ------------------------------------------------------------------------------------

function OfficesCard({ q }: { q: PageQuery }) {
  const qc = useQueryClient();
  const settings = useQuery(purchaseSettingsQuery);
  const [adding, setAdding] = useState(false);
  const page = q.data;
  const offices = page?.offices ?? [];
  const proposals = page?.proposals ?? [];
  // The server doesn't say which office a proposal belongs to: with one office it sits there;
  // otherwise under the list, and Confirm asks which office.
  const inline = offices.length === 1;
  return (
    <Panel
      title="Offices"
      help="Calls from a confirmed office network use that office's address. Kite proposes networks where your people sign in."
      actions={
        <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
          <Plus />
          Add office
        </Button>
      }
    >
      {!page ? (
        <PageState q={q} />
      ) : offices.length === 0 && proposals.length === 0 ? (
        <p className="px-4 pt-1 pb-4 text-caption text-ink-3">No offices yet</p>
      ) : (
        <ul className="divide-y divide-hairline pb-1">
          {offices.map((o) => (
            <OfficeRow key={o.place.id} office={o} proposals={inline ? proposals : []} />
          ))}
          {!inline && proposals.length ? (
            <li className="flex flex-col gap-2 px-4 py-3">
              {proposals.map((n) => (
                <Proposal key={n.id} network={n} offices={offices} />
              ))}
            </li>
          ) : null}
        </ul>
      )}
      {adding ? (
        <AddressDialog
          settings={settings.data}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void qc.invalidateQueries({ queryKey: e911Keys.page });
            toast("Office added.");
          }}
        />
      ) : null}
    </Panel>
  );
}

function OfficeRow({ office, proposals }: { office: Office; proposals: OfficeNetwork[] }) {
  const [adding, setAdding] = useState(false);
  const confirmed = office.networks.filter((n) => n.state === "confirmed");
  return (
    <li className="flex gap-3 px-4 py-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-fill-1 text-ink-3">
        <Building2 className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-ink text-ui">{office.place.label}</span>
            <span className="truncate text-caption text-ink-3">{office.place.address}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Add network to ${office.place.label}`}
            onClick={() => setAdding(true)}
          >
            <Plus />
            Add network
          </Button>
        </div>
        {confirmed.length ? (
          <div className="flex flex-wrap gap-1.5">
            {confirmed.map((n) => (
              <NetworkChip key={n.id} network={n} />
            ))}
          </div>
        ) : null}
        {adding ? <AddNetwork officeId={office.place.id} onDone={() => setAdding(false)} /> : null}
        {proposals.map((n) => (
          <Proposal key={n.id} network={n} offices={[office]} />
        ))}
      </div>
    </li>
  );
}

const CHIP =
  "inline-flex h-6 items-center rounded-pill bg-fill-1 px-2.5 font-mono text-caption text-ink-2 shadow-[inset_0_0_0_1px_var(--vd-hairline)]";

function NetworkChip({ network }: { network: OfficeNetwork }) {
  const qc = useQueryClient();
  const text = networkText(network.cidr);
  const remove = useMutation({
    mutationFn: () => e911.removeNetwork(network.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: e911Keys.page }),
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <Menu
      items={[{ label: "Remove network", danger: true, onSelect: () => remove.mutate() }]}
      trigger={
        <button
          type="button"
          aria-label={`Network ${text}`}
          className={`${CHIP} press outline-none hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--vd-focus)]`}
        >
          {text}
        </button>
      }
    />
  );
}

function AddNetwork({ officeId, onDone }: { officeId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => e911.addNetwork({ cidr: value.trim(), office_id: officeId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: e911Keys.page });
      onDone();
    },
    onError: (e) => setError(errorMessage(e)),
  });
  return (
    <form
      noValidate
      className="flex flex-col gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim() && !add.isPending) add.mutate();
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          autoFocus
          aria-label="Network"
          placeholder="203.0.113.0/24"
          className="w-52 font-mono"
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              onDone();
            }
          }}
        />
        <Button type="submit" size="sm" disabled={!value.trim()} busy={add.isPending}>
          Add
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-caption text-danger-ink">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function Proposal({ network, offices }: { network: OfficeNetwork; offices: Office[] }) {
  const qc = useQueryClient();
  const text = networkText(network.cidr);
  const done = () => qc.invalidateQueries({ queryKey: e911Keys.page });
  const confirm = useMutation({
    mutationFn: (officeId: string) => e911.confirmNetwork(network.id, officeId),
    onSuccess: done,
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const dismiss = useMutation({
    mutationFn: () => e911.dismissNetwork(network.id),
    onSuccess: done,
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const busy = confirm.isPending || dismiss.isPending;
  const only = offices.length === 1 ? offices[0] : undefined;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-surface-2 px-3 py-2.5">
      <div className="flex min-w-48 flex-1 flex-wrap items-center gap-2">
        <span className="font-medium text-ink text-ui">Proposed network</span>
        <span className={CHIP}>{text}</span>
      </div>
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => dismiss.mutate()}>
        Dismiss
      </Button>
      {only ? (
        <Button
          size="sm"
          busy={confirm.isPending}
          disabled={busy}
          onClick={() => confirm.mutate(only.place.id)}
        >
          Confirm
        </Button>
      ) : (
        <Menu
          align="end"
          items={offices.map((o) => ({ label: o.place.label, onSelect: () => confirm.mutate(o.place.id) }))}
          trigger={
            <Button size="sm" busy={confirm.isPending} disabled={busy || offices.length === 0}>
              Confirm
            </Button>
          }
        />
      )}
    </div>
  );
}

// 911 contacts -------------------------------------------------------------------------------

function ContactsCard() {
  const qc = useQueryClient();
  const q = useQuery(e911ContactsQuery);
  const users = useQuery(usersQuery);
  const [picking, setPicking] = useState(false);
  const [addingEmail, setAddingEmail] = useState(false);
  const c = q.data;
  const save = useMutation({
    mutationFn: (next: { members: string[]; emails: string[] }) => e911.setContacts(next),
    onSuccess: (v) => qc.setQueryData(e911Keys.contacts, v),
  });
  // An empty member list means every Admin, so the default is sent as none.
  const memberIds = (x: EmergencyContacts) => (x.default_admins ? [] : x.members.map((m) => m.user_id));
  const roleOf = (userId: string) => {
    const u = users.data?.find((x) => x.id === userId);
    if (!u) return c?.default_admins ? "Admin" : undefined;
    return roleNames(u.roles).at(-1);
  };

  return (
    <Panel title="911 contacts" help="Alerted in Kite, by push and by email when anyone here dials 911.">
      {!c ? (
        q.isPending ? (
          <Loading />
        ) : (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        )
      ) : (
        <>
          <ul className="divide-y divide-hairline">
            {c.members.map((m) => {
              const role = roleOf(m.user_id);
              return (
                <li key={m.user_id} className="flex min-h-12 items-center gap-2.5 px-4">
                  <Avatar name={m.name} userId={m.user_id} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-ink text-ui">{m.name || m.email}</span>
                  {role ? <Badge>{role}</Badge> : null}
                </li>
              );
            })}
            {c.emails.map((email) => {
              const last = c.members.length + c.emails.length <= 1;
              const remove = (
                <IconButton
                  size="sm"
                  label={`Remove ${email}`}
                  icon={<X />}
                  disabled={last || save.isPending}
                  tooltip={!last}
                  onClick={() =>
                    save.mutate(
                      { members: memberIds(c), emails: c.emails.filter((e) => e !== email) },
                      { onError: (e) => toast(errorMessage(e), "danger") },
                    )
                  }
                />
              );
              return (
                <li key={email} className="flex min-h-12 items-center gap-2.5 px-4">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-pill bg-fill-2 text-ink-3">
                    <Mail className="size-3.5" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-ink text-ui">{email}</span>
                  {last ? (
                    <Tooltip content="Keep at least one contact.">
                      <span>{remove}</span>
                    </Tooltip>
                  ) : (
                    remove
                  )}
                </li>
              );
            })}
            {addingEmail ? (
              <li className="px-4 py-2.5">
                <AddEmail
                  onDone={() => setAddingEmail(false)}
                  onAdd={(email) =>
                    save.mutateAsync({ members: memberIds(c), emails: [...c.emails, email] }).then(() => {
                      setAddingEmail(false);
                    })
                  }
                />
              </li>
            ) : null}
          </ul>
          <div className="flex items-center gap-1 border-hairline border-t px-2 py-2">
            <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
              <Plus />
              Add member
            </Button>
            {c.emails.length < c.max_emails && !addingEmail ? (
              <Button variant="ghost" size="sm" onClick={() => setAddingEmail(true)}>
                <Plus />
                Add email
              </Button>
            ) : null}
            <span className="ml-auto px-2 text-caption text-ink-3 tnum">
              {c.emails.length} of {c.max_emails}
            </span>
          </div>
          {picking ? (
            <PeoplePicker
              open
              onOpenChange={(o) => {
                if (!o) setPicking(false);
              }}
              title="911 contacts"
              loading={users.isPending}
              error={users.isError ? errorMessage(users.error) : null}
              onRetry={() => users.refetch()}
              people={(users.data ?? [])
                .filter((u) => u.status === "active")
                .map((u) => ({ id: u.id, name: u.name, email: u.email, role: roleNames(u.roles).at(-1) }))}
              selected={c.members.map((m) => m.user_id)}
              onSave={async ({ selected }) => {
                if (selected.length === 0) throw new Error("Keep at least one contact.");
                try {
                  await save.mutateAsync({ members: selected, emails: c.emails });
                } catch (e) {
                  throw new Error(errorMessage(e));
                }
              }}
            />
          ) : null}
        </>
      )}
    </Panel>
  );
}

function AddEmail({ onAdd, onDone }: { onAdd: (email: string) => Promise<void>; onDone: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      noValidate
      className="flex flex-col gap-1"
      onSubmit={async (e) => {
        e.preventDefault();
        const email = value.trim();
        if (!email || busy) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setError(`${email} is not an email address.`);
          return;
        }
        setBusy(true);
        try {
          await onAdd(email);
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          type="email"
          aria-label="Email"
          placeholder="frontdesk@company.com"
          className="min-w-0 flex-1"
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              onDone();
            }
          }}
        />
        <Button type="submit" size="sm" disabled={!value.trim()} busy={busy}>
          Add
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-caption text-danger-ink">
          {error}
        </p>
      ) : null}
    </form>
  );
}

// People -------------------------------------------------------------------------------------

const SOURCE_WORD = {
  office_network: "network",
  device: "device",
  saved: "saved",
  picked: "picked",
} as const;

/** On a network no office matches, with no saved address: 911 uses the company address. */
function unknownPlace(p: PersonPlace): boolean {
  return !p.phone_app_only && p.source === "company";
}

/** "Main office, network", "Home, saved", or "Company address". */
function placeText(p: PersonPlace): string {
  if (!p.place || !p.source || p.source === "company") return "Company address";
  return `${p.place.label}, ${SOURCE_WORD[p.source]}`;
}

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

/** "Today 8:02 AM", "Yesterday", "Oct 30". */
function updatedText(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days <= 0) return `Today ${timeFmt.format(d)}`;
  if (days === 1) return "Yesterday";
  return dayFmt.format(d);
}

function PeopleCard({ q }: { q: PageQuery }) {
  const [filter, setFilter] = useState<"all" | "unknown">("all");
  const page = q.data;
  const people = page?.people ?? [];
  const unknown = people.filter(unknownPlace);
  const rows = filter === "unknown" ? unknown : people;
  const companyAddress = page?.offices[0]?.place.address ?? "";
  return (
    <Panel
      title="People"
      help="Where Kite places each person for 911 right now. Unknown place: on a network no office matches, with no saved address, so 911 uses the company address."
      actions={
        page ? (
          <>
            <ToggleChip pressed={filter === "all"} onPressedChange={() => setFilter("all")}>
              All {people.length}
            </ToggleChip>
            <ToggleChip pressed={filter === "unknown"} onPressedChange={() => setFilter("unknown")}>
              Unknown place {unknown.length}
            </ToggleChip>
          </>
        ) : null
      }
    >
      {!page ? (
        <PageState q={q} />
      ) : (
        <div className="overflow-x-auto pb-1">
          <Table>
            <THead>
              <Tr>
                <Th>Person</Th>
                <Th>Place</Th>
                <Th className="hidden md:table-cell">Address</Th>
                <Th numeric className="hidden md:table-cell">
                  Updated
                </Th>
              </Tr>
            </THead>
            <TBody>
              {rows.length === 0 ? (
                <Tr>
                  <Td colSpan={4} muted>
                    No one here
                  </Td>
                </Tr>
              ) : (
                rows.map((p) => (
                  <Tr key={p.user_id}>
                    <Td>
                      <span className="flex min-w-0 items-center gap-2.5">
                        <Avatar name={p.name || p.email} userId={p.user_id} size="sm" />
                        <span className="truncate">{p.name || p.email}</span>
                      </span>
                    </Td>
                    <Td>
                      {p.phone_app_only ? (
                        <span className="inline-flex items-center gap-1.5 text-ink-3">
                          Phone app
                          <InfoButton kind="help" label="About Phone app">
                            911 from the phone app uses the phone's cellular service.
                          </InfoButton>
                        </span>
                      ) : (
                        <span className="inline-flex flex-wrap items-center gap-2">
                          {placeText(p)}
                          {unknownPlace(p) ? <Badge>Unknown place</Badge> : null}
                        </span>
                      )}
                    </Td>
                    <Td className="hidden md:table-cell">
                      {p.phone_app_only ? "" : (p.place?.address ?? companyAddress)}
                    </Td>
                    <Td numeric muted className="hidden md:table-cell">
                      {p.phone_app_only ? "" : updatedText(p.since)}
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </div>
      )}
    </Panel>
  );
}
